'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import useCountries from '@framework/hooks/useCountries';
import sharedConfig from '@dataroom/config';
import { DataroomTenant } from '@dataroom/tenant/DataroomTenant';
import CountryOfBusinessRepository from './CountryOfBusinessRepository';
import DataroomErrorHandler from '@dataroom/error-handler';

const useCountryOfBusiness = (tenant: DataroomTenant, contactId: number) => {
  const container = getContainer();

  const {
    getCountriesList,
    collection: countries,
    isFetching: isCountriesFetching,
  } = useCountries();

  const repository = container.get<CountryOfBusinessRepository>(CountryOfBusinessRepository);
  const isCountryOfBusinessRequired = sharedConfig[tenant].options.isCountryOfBusinessEnabled;

  const [countryId, setCountryId] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetched, setIsFetched] = useState(false);

  const getCountry = async () => {
    setIsFetching(true);

    try {
      const response = await repository.getCountry(tenant);
      setCountryId(response.countryId);
      return response.countryId;
    } catch (error) {
      container.get(DataroomErrorHandler).handleError(error);
      return null;
    } finally {
      setIsFetching(false);
      setIsFetched(true);
    }
  };

  const getCountryByContactId = async (contactId: number) => {
    setIsFetching(true);

    try {
      const response = await repository.getCountryByContactId(tenant, contactId);
      setCountryId(response.countryId);
      return response.countryId;
    } catch (error) {
      container.get(DataroomErrorHandler).handleError(error);
      return null;
    } finally {
      setIsFetching(false);
      setIsFetched(true);
    }
  };

  const updateCountry = async (countryCode: string) => {
    setIsFetching(true);
    try {
      await repository.updateCountry(tenant, countryCode);
      setCountryId(countryCode);
    } catch (error) {
      container.get(DataroomErrorHandler).handleError(error);
    } finally {
      setIsFetching(false);
    }
  };

  const confirmRestrictedCountryIp = async () => {
    setIsFetching(true);
    try {
      await repository.confirmRestrictedCountryIp(tenant);
    } catch (error) {
      container.get(DataroomErrorHandler).handleError(error);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (isCountryOfBusinessRequired) {
      getCountriesList();
      if (contactId) {
        getCountryByContactId(contactId);
      } else {
        getCountry();
      }
    }
  }, [isCountryOfBusinessRequired]);

  return {
    tenant,
    countries,
    isCountriesFetching,
    countryId,
    isFetching,
    isFetched,
    getCountry,
    getCountryByContactId,
    updateCountry,
    confirmRestrictedCountryIp,
  };
};

export type CountryOfBusinessContextType = ReturnType<typeof useCountryOfBusiness>;

export const CountryOfBusinessContext = createContext<CountryOfBusinessContextType | undefined>(undefined);

export const useCountryOfBusinessContext = (): CountryOfBusinessContextType => {
  const context = useContext(CountryOfBusinessContext);
  if (!context) {
    throw new Error('useCountryOfBusinessContext must be used within a CountryOfBusinessContextProvider');
  }
  return context;
};

interface IProp {
  children: React.ReactNode,
  tenant?: DataroomTenant,
  contactId?: number,
}

const CountryOfBusinessContextProvider = ({
  tenant,
  children,
  contactId,
}: IProp) => {
  const contextValue = useCountryOfBusiness(tenant!, contactId!);

  return (
    <CountryOfBusinessContext.Provider value={ contextValue }>
      { children }
    </CountryOfBusinessContext.Provider>
  );
};

export default CountryOfBusinessContextProvider;
